/* ----------------------------------------------------------------------------
 * recommendModel — the PURE, DOM-free decision core for conversational
 * push/publish/write (owner GO 12-10-2026 20:30 UTC, "go", for the spec recorded
 * verbatim at 20:29 UTC in docs/brain/governance/owner-decision-aura-publish-choice-
 * 2026-10-10.md: "ask aura - hey what do you recommend - aura picks up from what's
 * already there- suggesting blogs to publish in priority order- then asks amd listens
 * for answer -- eg shally write them all now or tell me the number or name --eg
 * listed order number displayed on screen or just a few words of the blog title she
 * said..or say all").
 *
 *   - detectRecommendAsk: "what do you recommend?" / "what should I publish?" /
 *     "any suggestions?" -- whole-utterance only, so a real advice question
 *     ("what do you recommend for shamanic healing pricing?") still reaches the
 *     advisor;
 *   - detectPendingListAsk: "show me my pending blogs" (also the STT mishears
 *     "blocks" / "logs", ONLY inside a pending/ready/queued ask);
 *   - buildRecommendList: ONE numbered, priority-ordered list from what already
 *     exists: ready drafts first (publish), then SEO-Opportunities topics (write);
 *   - parseListSelection: numbers ("2", "1 and 3", "the first", "number 4"), a few
 *     title words ("the kambo one"), "all", "no", a bare yes -- an ambiguous title
 *     returns the two candidates for a one-line clarifier.
 * Nothing here executes anything: publishing keeps the P1 read-back + pure-yes rule,
 * writing keeps the server cap check (useRecommendVoice hands off to those flows).
 * -------------------------------------------------------------------------- */

import { isTurnClose } from '../wake/wakeMachine.ts';
import { isPureAffirmative, speakableTitle } from './batchPublishModel.ts';

export type RecommendScope = 'all' | 'publish' | 'write';

export interface RecommendAsk {
  scope: RecommendScope;
  /** 'pending' = "show my pending blogs" (publish candidates only). */
  source: 'recommend' | 'pending';
}

export interface RecommendItem {
  /** 1-based number shown on screen and spoken. */
  n: number;
  kind: 'publish' | 'write';
  title: string;
  /** publish: the ready BookedCentral draft. */
  draftId?: number;
  /** write: the suggestion card index (server-side proposal row). */
  index?: number;
  keyword?: string;
  /** publish: P1 card state ("on_site_as_draft" = already on the site as a WP draft). */
  state?: string;
}

export const RECOMMEND_NONE = 'I don’t have anything to recommend for that site right now.';
export const RECOMMEND_CANCEL_ACK = 'Okay, I’ll leave them for now.';
export const RECOMMEND_WHICH = 'Which ones? Say the numbers, a few words of the title, or all.';
export const RECOMMEND_OWNER_OPERATOR_ONLY = 'Only workspace owners and operators can publish or write blogs with Aura.';
export const RECOMMEND_MAX_REASKS = 1;

function norm(transcript: string): string {
  const s = (transcript ?? '')
    .replace(/['’‘`]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s === '' ? '' : ' ' + s + ' ';
}

/** Leading wake/echo words that may survive into a request ("Ora. What are …"). */
function dropLead(p: string): string {
  let s = p;
  for (let i = 0; i < 3; i += 1) {
    const next = s.replace(/^ (?:hey |hi |ok |okay )?(?:aura|ora)(?= )/, '').replace(/^ (?:hey|so|ok|okay|right|um|uh)(?= )/, '');
    if (next === s) break;
    s = next;
  }
  return s;
}

const SITE_TAIL = '(?: for (?:my |our |the )?(?:site|website|blog|domain))?';
const TIME_TAIL = '(?: (?:next|now|today|first|then))?';

/** "what do you recommend?" family. Whole utterance (after wake words), so extra
 *  content ("… for shamanic healing pricing") stays an advice question. Pure. */
export function detectRecommendAsk(transcript: string): RecommendAsk | null {
  const p = dropLead(norm(transcript));
  if (p.trim() === '') return null;
  const verbScope = (v: string | undefined): RecommendScope => (v === 'write' || v === 'create' ? 'write' : v ? 'publish' : 'all');
  let m = new RegExp(`^ (?:so )?what (?:do|would|can|could) you (?:recommend|suggest)(?: (?:i|we) (?:do|(publish|write|post|push|create)))?${TIME_TAIL}${SITE_TAIL}${TIME_TAIL} $`).exec(p);
  if (m) return { scope: verbScope(m[1]), source: 'recommend' };
  m = new RegExp(`^ what should (?:i|we) (publish|write|post|push|create)${TIME_TAIL}${SITE_TAIL}${TIME_TAIL} $`).exec(p);
  if (m) return { scope: verbScope(m[1]), source: 'recommend' };
  m = new RegExp(`^ (?:what|which) (?:blogs?|posts?|articles?|drafts?) (?:do|would|should) (?:you (?:recommend|suggest)|(?:i|we) (publish|write|post|push))(?: (?:i|we) (publish|write|post|push))?${TIME_TAIL}${SITE_TAIL}${TIME_TAIL} $`).exec(p);
  if (m) return { scope: verbScope(m[1] ?? m[2]), source: 'recommend' };
  if (new RegExp(`^ (?:(?:do you have|have you got|got|any) )?(?:any )?(?:suggestions|recommendations|ideas)(?: for (?:me|today|us))?${SITE_TAIL} $`).test(p)) return { scope: 'all', source: 'recommend' };
  if (/^ what (?:are|re) your (?:recommendations|suggestions)(?: for today)? $/.test(p)) return { scope: 'all', source: 'recommend' };
  if (/^ (?:give me |make )?(?:a |some |your )?(?:recommendations?|suggestions?) $/.test(p)) return { scope: 'all', source: 'recommend' };
  return null;
}

/** STT mishears of "blogs" accepted ONLY inside a pending-list ask (diagnosis F3). */
const PENDING_STATUS = / (pending|ready|queued|in the queue|on the queue|waiting|unpublished|not published|not yet published) /;

/** "show me my pending blogs" / "what are the pending blogs?" (+ blocks/logs). Read
 *  ask only: an action verb ("push", "write", "can you publish") is never matched. */
export function detectPendingListAsk(transcript: string): RecommendAsk | null {
  let p = dropLead(norm(transcript));
  if (p.trim() === '' || !PENDING_STATUS.test(p)) return null;
  p = p.replace(/ (blocks?|logs?|blog s) /g, ' blogs ');
  if (!/ (blogs?|drafts?|posts?|articles?|blog posts?) /.test(p)) return null;
  if (/ (push|send|upload|release|write|create|update|delete|remove|schedule) /.test(p)) return null;
  if (/^ (?:please )?(?:publish|post) /.test(p) || / (?:can|could|would|will) you (?:please )?(?:publish|post) /.test(p)) return null;
  if (!/ (what|which|show|list|any|tell me|do i have|have i got|are there|is there|whats|how many|see|display|my pending|pending blogs|pending drafts) /.test(p)) return null;
  return { scope: 'publish', source: 'pending' };
}

/** ONE priority-ordered list: ready drafts first (they only need publishing), then
 *  topics to write (SEO Opportunities order). Numbered from 1. Pure. */
export function buildRecommendList(
  drafts: readonly { draft_id: number; title: string; state?: string }[],
  topics: readonly { index: number; keyword: string; working_title?: string }[],
): RecommendItem[] {
  const out: RecommendItem[] = [];
  for (const d of drafts) out.push({ n: out.length + 1, kind: 'publish', title: d.title, draftId: d.draft_id, state: d.state });
  for (const t of topics) out.push({ n: out.length + 1, kind: 'write', title: (t.working_title ?? '').trim() || t.keyword, index: t.index, keyword: t.keyword });
  return out;
}

/** The closing question, by what the list holds (owner wording: "shall i write them
 *  all now or tell me the number or name"). */
export function recommendQuestion(items: readonly RecommendItem[]): string {
  const pub = items.some((i) => i.kind === 'publish');
  const wr = items.some((i) => i.kind === 'write');
  const verb = pub && wr ? 'publish and write' : pub ? 'publish' : 'write';
  if (items.length === 1) return `Shall I ${verb} it now?`;
  return `Shall I ${verb} them all now, or tell me the number or name?`;
}

function quoteList(ts: string[]): string {
  const q = ts.map((t) => `‘${t}’`);
  return q.length <= 1 ? (q[0] ?? '') : q.slice(0, -1).join(', ') + ' and ' + q[q.length - 1];
}

/** Spoken line: the top 3 briefly (titles shortened), then the question. The whole
 *  numbered list is on screen. Pure. */
export function recommendSpokenLine(items: readonly RecommendItem[], siteName: string, source: RecommendAsk['source']): string {
  if (items.length === 0) return RECOMMEND_NONE;
  const top = items.slice(0, 3);
  const pubs = top.filter((i) => i.kind === 'publish').map((i) => speakableTitle(i.title, 48));
  const wrs = top.filter((i) => i.kind === 'write').map((i) => speakableTitle(i.keyword ?? i.title, 48));
  if (source === 'pending') {
    const n = items.length;
    const lead = n === 1 ? `Your next ready draft for ${siteName} is ${quoteList(pubs)}.` : `Your next ${n} ready drafts for ${siteName} are on screen, starting with ${quoteList(pubs.slice(0, 2))}.`;
    return `${lead} ${recommendQuestion(items)}`;
  }
  const parts: string[] = [];
  if (pubs.length > 0) parts.push('publish ' + quoteList(pubs));
  if (wrs.length > 0) parts.push('write ' + quoteList(wrs));
  return `For ${siteName}, I’d ${parts.join(', then ')}. ${recommendQuestion(items)}`;
}

// ---- answer resolution -------------------------------------------------------

export type ListSelection =
  | { kind: 'all' }
  | { kind: 'some'; ns: number[] }
  | { kind: 'ambiguous'; candidates: number[] }
  | { kind: 'no' }
  | { kind: 'bare_yes' }
  | { kind: 'none' };

const NUM_WORDS: Readonly<Record<string, number>> = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10,
  '1st': 1, '2nd': 2, '3rd': 3, '4th': 4, '5th': 5, '6th': 6, '7th': 7, '8th': 8, '9th': 9, '10th': 10,
  to: 2, too: 2, for: 4,
};
/** "to"/"too"/"for" count as numbers ONLY when the answer has no other words (STT "Two." -> "To."). */
const WEAK_NUM = new Set(['to', 'too', 'for']);
const SEL_NO = [' no ', ' nope ', ' nah ', ' cancel ', ' none ', ' neither ', ' not now ', ' leave it ', ' leave them ', ' never mind ', ' nevermind ', ' stop ', ' dont ', ' not yet '];
const SEL_ALL = / (all|everything|all of them|every one|each one|the lot|all three|all five) /;
const SEL_FILLER = new Set([
  'the', 'number', 'numbers', 'no', 'and', 'plus', 'please', 'aura', 'ora', 'ok', 'okay', 'um', 'uh', 'er', 'erm', 'publish', 'push',
  'write', 'post', 'do', 'just', 'them', 'it', 'blog', 'blogs', 'posts', 'draft', 'drafts', 'article', 'articles', 'ones', 'only',
  'also', 'then', 'lets', 'go', 'with', 'me', 'can', 'you', 'could', 'i', 'want', 'would', 'like', 'choose', 'pick', 'take',
  'item', 'items', 'option', 'options', 'yes', 'yeah', 'a', 'an', 'of', 'that', 'this', 'those', 'these', 'is', 'are', 'about',
  'on', 'in', 'one', 'save', 'as', 'live', 'now', 'start', 'shall', 'we', 'thanks', 'thank', 'mean', 'meant', 'said', 'called',
]);

const TALK_LEAD = /^ (?:(?:hey |ok |okay )?(?:aura |ora ))?(?:tell me|what|whats|why|how|more on|more about|explain|is it|is there|are there|can you tell|could you tell|do you|did you|which is|who) /;
const stem = (w: string): string => w.replace(/(?:ing|es|s)$/, '');
function titleWords(t: string): string[] {
  return norm(t).trim().split(' ').filter((w) => w.length > 2 && !SEL_FILLER.has(w)).map(stem);
}
function wordHit(w: string, words: readonly string[]): boolean {
  return words.some((x) => x === w || (x.length >= 4 && w.length >= 4 && (x.startsWith(w) || w.startsWith(x))));
}

/** Resolve an answer against the numbered on-screen list. Pure. */
export function parseListSelection(transcript: string, items: readonly { n: number; title: string; keyword?: string }[]): ListSelection {
  const p = norm(transcript);
  if (p === '' || items.length === 0) return { kind: 'none' };
  if (isTurnClose(transcript) || SEL_NO.some((t) => p.includes(t))) return { kind: 'no' };
  if (SEL_ALL.test(p) || (items.length === 2 && / both /.test(p))) return { kind: 'all' };
  if (isPureAffirmative(p)) return { kind: 'bare_yes' };
  // Fable IVG C3: a question / talk lead is conversation, never a pick ("tell me more on reiki",
  // "what about the reiki one?", "why the second?") -- it can never spend on a write.
  if (TALK_LEAD.test(p)) return { kind: 'none' };
  const toks = p.trim().split(' ');
  const nums: number[] = [];
  const words: string[] = [];
  let sawOne = false;
  for (const tok of toks) {
    if (/^\d+$/.test(tok)) { nums.push(parseInt(tok, 10)); continue; }
    if (tok === 'last') { nums.push(items.length); continue; }
    if (tok === 'one') { sawOne = true; continue; }
    if (NUM_WORDS[tok] !== undefined && !WEAK_NUM.has(tok)) { nums.push(NUM_WORDS[tok]); continue; }
    if (WEAK_NUM.has(tok)) continue;
    if (!SEL_FILLER.has(tok) && tok.length > 2) words.push(stem(tok));
  }
  if (words.length === 0) {
    if (nums.length === 0 && sawOne) nums.push(1);
    if (nums.length === 0) {
      const weak = toks.filter((t) => WEAK_NUM.has(t));
      if (weak.length === 1 && toks.filter((t) => !SEL_FILLER.has(t) && !WEAK_NUM.has(t)).length === 0) nums.push(NUM_WORDS[weak[0]]);
    }
  }
  const picked = new Set<number>();
  for (const n of nums) if (n >= 1 && n <= items.length) picked.add(n);
  if (words.length > 0) {
    let best = 0;
    const scores = items.map((it) => {
      const tw = Array.from(new Set([...titleWords(it.title), ...titleWords(it.keyword ?? '')]));
      const s = words.filter((w) => wordHit(w, tw)).length;
      if (s > best) best = s;
      return s;
    });
    // Fable IVG C3: a title-word pick needs >= 2 hits or >= 0.6 of the spoken content words
    // (as matchDraftTitle); a looser hit is not a pick. Equal candidates -> ONE clarifier naming all.
    if (best > 0 && (best >= 2 || best / words.length >= 0.6)) {
      const top = items.filter((_, i) => scores[i] === best);
      if (top.length >= 2 && picked.size === 0) return { kind: 'ambiguous', candidates: top.slice(0, 4).map((t) => t.n) };
      if (top.length === 1) picked.add(top[0].n);
    }
  }
  if (picked.size === 0) return { kind: 'none' };
  return { kind: 'some', ns: Array.from(picked).sort((a, b) => a - b) };
}

/** One-line clarifier naming every equal candidate (up to 4) by number and short title. */
export function clarifyLine(cands: readonly { n: number; title: string }[]): string {
  const parts = cands.map((c) => `${c.n}, ‘${speakableTitle(c.title, 40)}’`);
  const list = parts.length <= 1 ? (parts[0] ?? '') : parts.slice(0, -1).join(', ') + ', or ' + parts[parts.length - 1];
  return `Did you mean ${list}?`;
}

export { stripQueueRef } from './batchPublishModel.ts';
